import { Button, useToast } from "@design";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Upload } from "lucide-react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { errorMessage, listSpaceKnowledgeQueryKey, uploadSpaceFilesMutation } from "@/api";

/** Uploads files to a space's knowledge (editors only: the backend enforces it). Used in Knowledge › Files and settings. */
export function UploadSpaceFilesButton({ spaceUuid }: Readonly<{ spaceUuid: string }>) {
  const { t } = useTranslation("spaces", { keyPrefix: "settings.knowledge" });
  const toast = useToast();
  const queryClient = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const path = { space_uuid: spaceUuid };
  const upload = useMutation({
    ...uploadSpaceFilesMutation(),
    onSuccess: () => toast.success(t("uploaded")),
    onError: (error) => toast.error(errorMessage(error, t("uploadFailed"))),
    onSettled: () => queryClient.invalidateQueries({ queryKey: listSpaceKnowledgeQueryKey({ path }) }),
  });

  return (
    <>
      <Button
        variant="secondary"
        size="sm"
        leadingIcon={<Upload size={14} />}
        loading={upload.isPending}
        onClick={() => input.current?.click()}
      >
        {upload.isPending ? t("uploading") : t("upload")}
      </Button>
      <input
        ref={input}
        type="file"
        multiple
        hidden
        aria-label={t("upload")}
        onChange={(event) => {
          const chosen = Array.from(event.target.files ?? []);
          event.target.value = "";
          if (chosen.length > 0) upload.mutate({ path, body: { files: chosen } });
        }}
      />
    </>
  );
}
